import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../utils/api-client';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    setFormError(null);

    try {
      const response = await apiClient.post('/api/users/password', { email }, { showErrors: false });
      if (!response.ok) {
        const errorData = await response.json() as { error?: string };
        throw new Error(errorData.error || t('auth.unexpectedError'));
      }
      setSubmitted(true);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('auth.unexpectedError');
      setFormError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex justify-center items-center my-8">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t('auth.forgotPasswordSentTitle')}</CardTitle>
            <CardDescription>{t('auth.forgotPasswordSentDescription', { email })}</CardDescription>
          </CardHeader>
          <CardFooter>
            <Link to="/sign-in" className="w-full">
              <Button variant="outline" className="w-full">
                {t('auth.backToSignIn')}
              </Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center my-8">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('auth.forgotPasswordTitle')}</CardTitle>
          <CardDescription>{t('auth.forgotPasswordDescription')}</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {formError && (
              <div className="p-3 rounded-md bg-destructive/15 text-destructive text-sm">
                {formError}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setEmail(e.target.value);
                  if (formError) setFormError(null);
                }}
                required
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? t('auth.sendingResetLink') : t('auth.sendResetLink')}
            </Button>
            <p className="text-sm text-center text-muted-foreground">
              <Link to="/sign-in" className="text-primary hover:underline">
                {t('auth.backToSignIn')}
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
